import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { watchlistApi } from "@/services/api/endpoints/watchlist.api";

export interface WatchlistState {
  symbols: string[];
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: WatchlistState = {
  symbols: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchWatchlist = createAsyncThunk(
  "watchlist/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const symbols = await watchlistApi.getWatchlist();
      return symbols;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch watchlist");
    }
  },
);

export const addToWatchlist = createAsyncThunk(
  "watchlist/add",
  async (symbol: string, { rejectWithValue }) => {
    try {
      await watchlistApi.addToWatchlist(symbol);
      return symbol;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to add to watchlist");
    }
  },
);

export const removeFromWatchlist = createAsyncThunk(
  "watchlist/remove",
  async (symbol: string, { rejectWithValue }) => {
    try {
      await watchlistApi.removeFromWatchlist(symbol);
      return symbol;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to remove from watchlist");
    }
  },
);

export const batchUpdateWatchlist = createAsyncThunk(
  "watchlist/batchUpdate",
  async (symbols: string[], { rejectWithValue }) => {
    try {
      await watchlistApi.batchUpdateWatchlist(symbols);
      return symbols;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to update watchlist");
    }
  },
);

// Slice
const watchlistSlice = createSlice({
  name: "watchlist",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Watchlist
    builder
      .addCase(fetchWatchlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWatchlist.fulfilled, (state, action) => {
        state.loading = false;
        state.symbols = action.payload;
        state.error = null;
      })
      .addCase(fetchWatchlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Add to Watchlist
    builder
      .addCase(addToWatchlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToWatchlist.fulfilled, (state, action) => {
        state.loading = false;
        if (!state.symbols.includes(action.payload)) {
          state.symbols.push(action.payload);
        }
        state.error = null;
      })
      .addCase(addToWatchlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Remove from Watchlist
    builder
      .addCase(removeFromWatchlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFromWatchlist.fulfilled, (state, action) => {
        state.loading = false;
        state.symbols = state.symbols.filter(
          (symbol) => symbol !== action.payload,
        );
        state.error = null;
      })
      .addCase(removeFromWatchlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Batch Update Watchlist
    builder
      .addCase(batchUpdateWatchlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(batchUpdateWatchlist.fulfilled, (state, action) => {
        state.loading = false;
        state.symbols = action.payload;
        state.error = null;
      })
      .addCase(batchUpdateWatchlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Actions
export const { clearError } = watchlistSlice.actions;

// Selectors
export const selectWatchlist = (state: { watchlist: WatchlistState }) =>
  state.watchlist.symbols;
export const selectWatchlistLoading = (state: { watchlist: WatchlistState }) =>
  state.watchlist.loading;
export const selectWatchlistError = (state: { watchlist: WatchlistState }) =>
  state.watchlist.error;
export const selectIsInWatchlist =
  (symbol: string) => (state: { watchlist: WatchlistState }) =>
    state.watchlist.symbols.includes(symbol);

export default watchlistSlice.reducer;
